import type { BudgetRequestDto } from '@/modules/budget-management/engine/budget.types';
import { approveBudgetRequest, declineBudgetRequest } from '@/modules/budget-management/engine/budget.types';
import { formatCurrency } from '@/common/common_utils';
import { DateHelper } from '@/common/DateHelper';
import { PrimaryButtonMT, SecondaryButtonMT } from '@/components/buttons/Buttons';
import Spacing from '@/components/container/Spacing';
import { showSuccess, showError } from '@/modules/notification/engine/notification.library';
import { useAuthenticationStore } from '@/store';
import { useMutation } from '@tanstack/react-query';
import { X4bUserRoleEnum } from '@/constants/roles';

interface Props {
	data: BudgetRequestDto;
	onActionComplete?: () => void;
}

const formatRequestId = (id: number) => {
	return `XB-${id.toString().padStart(10, '0')}`;
};

const RequestRowExpansionTemplate = ({ data, onActionComplete }: Props) => {
	const hasRoles = useAuthenticationStore((state) => state.hasRoles);

	// Check if user is Admin or Manager
	const canApproveDecline = hasRoles([X4bUserRoleEnum.Manager]);

	const approveMutation = useMutation({
		mutationFn: () => approveBudgetRequest(data.Id),
		onSuccess: (response) => {
			if (response.Success) {
				showSuccess('Budget request approved successfully');
				onActionComplete?.();
			} else {
				showError(response.ErrorMessage || 'Failed to approve budget request');
			}
		},
		onError: (error) => {
			showError('An error occurred while approving the budget request');
			console.error('Error approving budget request:', error);
		},
	});

	const declineMutation = useMutation({
		mutationFn: () => declineBudgetRequest(data.Id),
		onSuccess: (response) => {
			if (response.Success) {
				showSuccess('Budget request declined successfully');
				onActionComplete?.();
			} else {
				showError(response.ErrorMessage || 'Failed to decline budget request');
			}
		},
		onError: (error) => {
			showError('An error occurred while declining the budget request');
			console.error('Error declining budget request:', error);
		},
	});

	const isPending = data.Status === 'Waiting for Approval';

	return (
		<div className="px-12 py-9 bg-surfaceContainerLow tablet:px-10 tablet:py-8">
			<div className="flex items-center gap-6 tablet:flex-wrap tablet:items-start">
				<div className="min-w-[360px] tablet:min-w-0 tablet:flex-1">
					<div className="text-neutral60 leading-6 font-poppins">Amount</div>
					<div className="text-[32px] font-semibold leading-10 font-poppins">
						{formatCurrency(data.Amount ?? 0)}
					</div>
				</div>

				<div className="min-w-0 flex-1 tablet:order-3 tablet:w-full tablet:flex-none">
					<div className="text-neutral60 leading-6 font-poppins">Reason</div>
					<div className="tracking-[0.25px] leading-6 font-poppins break-words">
						{data.Reason}
					</div>
				</div>
				{isPending && canApproveDecline && (
					<div className="flex items-center gap-4 ml-auto tablet:ml-0 tablet:flex-wrap tablet:justify-end">
						<PrimaryButtonMT
							id="approve"
							label="Approve"
							onClick={() => approveMutation.mutate()}
							disabled={approveMutation.isPending || declineMutation.isPending}
							loading={approveMutation.isPending}
						/>
						<SecondaryButtonMT
							id="decline"
							label="Decline"
							onClick={() => declineMutation.mutate()}
							disabled={approveMutation.isPending || declineMutation.isPending}
							loading={declineMutation.isPending}
						/>
					</div>
				)}
			</div>

			<hr className="border-surfaceContainerHighest my-6" />

			<div className="grid grid-cols-4 gap-4 tablet:grid-cols-2 tablet:gap-y-5">
				<div>
					<div className="text-neutral60 leading-6 font-poppins">Request ID</div>
					<div className="font-poppins">{formatRequestId(data.Id)}</div>
				</div>

				<div>
					<div className="text-neutral60 leading-6 font-poppins">
						Date requested
					</div>
					<div className="tracking-[0.25px] leading-6 font-poppins">
						{data.DateRequested ? DateHelper.formatDate(data.DateRequested) : ''}
					</div>
				</div>

				{(data.Status === 'Declined' || data.Status === 'Approved') && (
					<>
						<div>
							<div className="text-neutral60 leading-6 font-poppins">
								{data.Status === 'Declined'
									? 'Declined by'
									: 'Approved by'}
							</div>
							<div className="tracking-[0.25px] leading-6 font-poppins">
								{data.ApproverFullName}
							</div>
						</div>

						<div>
							<div className="text-neutral60 leading-6 font-poppins">
								{data.Status === 'Declined'
									? 'Date declined'
									: 'Date approved'}
							</div>
							<div className="tracking-[0.25px] leading-6 font-poppins">
								{data.DateApprovedDeclined ? DateHelper.formatDate(data.DateApprovedDeclined) : ''}
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default RequestRowExpansionTemplate;
